// KPI parser + payload helper, extracted so it can be imported by hooks/
// non-component code without violating react-refresh single-component rule.

// Split a description into (a) the user's prose and (b) the trailing
// machine-managed block of KPI: lines that the procedure editor writes.
// We use a stable delimiter comment so the description editor can roundtrip
// without losing user content. Anything after the delimiter is owned by
// the KPI editor; anything before is free text.
const KPI_BLOCK_MARKER = '<!-- kanbify:kpi -->'

// Strip legacy KPI marker + "KPI: <label>" lines from any description before
// display. KPI v4 stores fields in a dedicated JSON column; older rows still
// have them inline as text. Used in CardDetail, DashboardPersonalView,
// ProceduresView (Teo 2026-05-26).
export function stripKpiNoise(description) {
    if (!description) return ''
    return String(description)
        .replace(/<!--\s*kanbify:kpi\s*-->/gi, '')
        .split('\n')
        .filter((line) => !/^\s*KPI\b[\w\s-]*?(?:\d+)?\s*(?:->|:)/i.test(line))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

export function splitDescription(description) {
    if (!description) return { prose: '', kpiLines: [] }
    const idx = description.indexOf(KPI_BLOCK_MARKER)
    if (idx < 0) {
        // Legacy: KPI lines were inline in the prose. Pull them out so the
        // new editor can manage them, and trim trailing blank lines.
        const lines = description.split('\n')
        const prose = []
        const kpiLines = []
        for (const l of lines) {
            if (/^KPI[\w\s-]*?(?:\d+)?\s*(?:->|:)/i.test(l.trim())) {
                kpiLines.push(l.trim())
            } else {
                prose.push(l)
            }
        }
        return { prose: prose.join('\n').replace(/\n+$/, ''), kpiLines }
    }
    return {
        prose: description.slice(0, idx).replace(/\n+$/, ''),
        kpiLines: description
            .slice(idx + KPI_BLOCK_MARKER.length)
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean),
    }
}

export function joinDescription(prose, fields) {
    const trimmedProse = (prose || '').replace(/\n+$/, '')
    if (!fields || fields.length === 0) return trimmedProse
    const kpiLines = fields.map((f) => `KPI: ${f.label}`).join('\n')
    return `${trimmedProse ? trimmedProse + '\n\n' : ''}${KPI_BLOCK_MARKER}\n${kpiLines}`
}

export function parseRequiredKpiFields(description) {
    if (!description) return []
    return description
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const match = line.match(/^KPI[\w\s-]*?(?:\d+)?\s*(?:->|:)\s*(.+)$/i)
            if (!match) return null
            const label = match[1]?.trim()
            if (!label) return null
            const key = label
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '')
            return key ? { key, label } : null
        })
        .filter(Boolean)
}

export function parseKpiValue(raw) {
    if (raw == null) return { ok: false, n: null }
    const s = String(raw).trim()
    if (!s) return { ok: false, n: null }
    const n = Number(s)
    if (!Number.isFinite(n)) return { ok: false, n: null }
    if (n <= 0) return { ok: false, n }
    return { ok: true, n }
}

export function isKpiPayloadComplete(payload, userId, fields) {
    if (!fields || fields.length === 0) return true
    const byUser = payload?.by_user || {}
    const userValues = byUser[userId]?.values
    if (!userValues) return false
    for (const f of fields) {
        const { ok } = parseKpiValue(userValues[f.key])
        if (!ok) return false
    }
    return true
}

// KPI v4 (Teo 2026-05-26): returns the KPI fields for a card, preferring the
// per-card snapshot (cards.kpi_fields JSON) with per-field point multipliers.
// Falls back to legacy description parsing (`KPI: <label>` lines) with default
// points=1 for procedures predating v4. Each field shape: {key, label, points}.
export function getCardKpiFields(card) {
    if (!card) return []
    const raw = card.kpi_fields
    if (raw) {
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
            if (Array.isArray(parsed) && parsed.length) {
                return parsed
                    .filter((f) => f && f.key && f.label)
                    .map((f) => ({
                        key: f.key,
                        label: f.label,
                        points: Number.isFinite(Number(f.points)) ? Number(f.points) : 1,
                    }))
            }
        } catch { /* fall through to legacy */ }
    }
    return parseRequiredKpiFields(card.description).map((f) => ({ ...f, points: 1 }))
}

// Total points a card awards on completion given the values entered.
// KPI mode (kpi_fields present) → Σ(value × points_per_unit).
// Flat mode → uses card.point_value. Returns 0 if neither is set; callers may
// fall back to a legacy participation award (e.g. 5pt "Card completat").
export function computeCardPoints(card, values) {
    const fields = getCardKpiFields(card)
    if (fields.length > 0) {
        // KPI mode: Σ(value × points/unit). point_value devine "bonus completare"
        // opțional adăugat peste suma KPI (Teo 2026-05-26).
        const kpiSum = fields.reduce((sum, f) => {
            const v = Number(values?.[f.key])
            if (!Number.isFinite(v)) return sum
            const pts = Number.isFinite(Number(f.points)) ? Number(f.points) : 1
            return sum + v * pts
        }, 0)
        const bonus = Number(card?.point_value)
        return kpiSum + (Number.isFinite(bonus) && bonus > 0 ? bonus : 0)
    }
    const flat = Number(card?.point_value)
    return Number.isFinite(flat) ? flat : 0
}
